/** Locale-aware human-readable formatters used across pages. */

/** Canonical placeholder for missing / unknown / unrenderable values. Used
 *  by every formatter below and by any UI surface that needs an inline
 *  fallback. Exported so callers can render the same token instead of
 *  hard-coding a magic string. */
export const MISSING_VALUE = 'N/A';

/** Active locale for NUMBER/CURRENCY formatting (decimal & thousand separators),
 *  driven by the user's "Number format" profile preference. `undefined` means
 *  "use the browser/OS locale". Set once near the app root by the currency/
 *  preferences provider so every formatter below stays consistent (the
 *  time-vs-money mismatch came from leaving this implicit). Dates are NOT
 *  affected — that's a separate concern. */
let activeNumberLocale: string | undefined = undefined;

export function setActiveNumberLocale(locale: string | undefined): void {
  activeNumberLocale = locale && locale.trim() ? locale : undefined;
}

/** Capitalize only the first letter of a string ("transformers" ->
 *  "Transformers", "text-generation" -> "Text-generation"). Use this on
 *  API-supplied identifiers when displaying them in table cells so the
 *  UI reads like prose, not a config file. */
export function sentenceCase(value: string | null | undefined): string {
  if (!value) return '';
  if (value.length === 0) return value;
  return value[0].toUpperCase() + value.slice(1);
}

export function formatDate(value: string | null | undefined): string {
  if (!value) return MISSING_VALUE;
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return MISSING_VALUE;
  return new Intl.DateTimeFormat(undefined, { dateStyle: 'medium' }).format(d);
}

export function formatDateTime(value: string | null | undefined): string {
  if (!value) return MISSING_VALUE;
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return MISSING_VALUE;
  return new Intl.DateTimeFormat(undefined, {
    dateStyle: 'medium',
    timeStyle: 'short',
  }).format(d);
}

/** Compact relative time, e.g. "3 days ago" / "in 2 hours". Localized via
 *  Intl.RelativeTimeFormat; MISSING_VALUE for null/invalid input. */
export function formatRelativeTime(value: string | null | undefined): string {
  if (!value) return MISSING_VALUE;
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return MISSING_VALUE;
  const diffSeconds = (d.getTime() - Date.now()) / 1000;
  const rtf = new Intl.RelativeTimeFormat(undefined, { numeric: 'auto' });
  const steps: Array<[Intl.RelativeTimeFormatUnit, number]> = [
    ['year', 60 * 60 * 24 * 365],
    ['month', 60 * 60 * 24 * 30],
    ['week', 60 * 60 * 24 * 7],
    ['day', 60 * 60 * 24],
    ['hour', 60 * 60],
    ['minute', 60],
  ];
  for (const [unit, seconds] of steps) {
    if (Math.abs(diffSeconds) >= seconds) {
      return rtf.format(Math.trunc(diffSeconds / seconds), unit);
    }
  }
  return rtf.format(Math.trunc(diffSeconds), 'second');
}

/** Full date + time + timezone, e.g. "28 Jun 2026, 16:53:00 GMT+2".
 *  `timeStyle: 'long'` is what pulls in the timezone name. Used where the user
 *  needs an unambiguous "as of" timestamp (e.g. recommendations freshness). */
export function formatDateTimeTz(value: string | null | undefined): string {
  if (!value) return MISSING_VALUE;
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return MISSING_VALUE;
  return new Intl.DateTimeFormat(undefined, {
    dateStyle: 'medium',
    timeStyle: 'long',
  }).format(d);
}

export function formatTime(value: string | null | undefined): string {
  if (!value) return '';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '';
  return new Intl.DateTimeFormat(undefined, { hour: '2-digit', minute: '2-digit' }).format(d);
}

export function formatNumber(
  value: number | null | undefined,
  opts?: Intl.NumberFormatOptions,
): string {
  if (value === null || value === undefined || Number.isNaN(value)) return MISSING_VALUE;
  return new Intl.NumberFormat(activeNumberLocale, opts).format(value);
}

export function formatCurrency(value: number | null | undefined, currency = 'USD'): string {
  if (value === null || value === undefined || Number.isNaN(value)) return MISSING_VALUE;
  // Magnitude-based precision: amounts >= 1 read cleaner at 2 decimals, while
  // sub-unit prices (e.g. GPU €/hour like 0.1409) need 3 so genuinely different
  // values don't collapse to the same 2-decimal string. min == max keeps a
  // column's values at a uniform width, which also helps them line up.
  // (Display precision only — ranking is by score, not the rendered figure.)
  const fractionDigits = Math.abs(value) >= 1 ? 2 : 3;
  return new Intl.NumberFormat(activeNumberLocale, {
    style: 'currency',
    currency,
    minimumFractionDigits: fractionDigits,
    maximumFractionDigits: fractionDigits,
  }).format(value);
}

/** A money amount at exactly two decimals ("€0.00", "€1,250.00"), for what
 *  was paid, refunded or is held. `formatCurrency` above keeps three decimals
 *  under 1 for sub-cent GPU prices; billing amounts are whole cents. */
export function formatMoney(value: number | null | undefined, currency: string): string {
  if (value === null || value === undefined || Number.isNaN(value)) return MISSING_VALUE;
  return new Intl.NumberFormat(activeNumberLocale, {
    style: 'currency',
    currency,
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(value);
}

/** Render a millisecond duration in seconds, e.g. 4136 -> "4.14 s". Whole-
 *  generation latencies (generate / decode / p95) run to tens of seconds, so
 *  seconds read better than five-digit millisecond counts. Per-token and
 *  prefill latencies (ITL, TTFT) stay in ms via `formatNumber`.
 *  Returns MISSING_VALUE for null/undefined/NaN. */
export function formatSeconds(ms: number | null | undefined): string {
  if (ms === null || ms === undefined || Number.isNaN(ms)) return MISSING_VALUE;
  return `${new Intl.NumberFormat(activeNumberLocale, { maximumFractionDigits: 2 }).format(ms / 1000)} s`;
}

/** Per PR.md formatContextLength, applied to `contextWindowK` (already in K).
 *  Returns "8K", "32K", "128K"; "Unknown" for null/zero/negative.
 */
export function formatContextWindowK(value: number | null | undefined): string {
  if (value === null || value === undefined || value <= 0) return 'Unknown';
  if (value < 1) return `${value}K`;
  return `${value}K`;
}

/** Compact model-parameter formatter: "8B", "560M", "1.5T", etc. Drops
 *  trailing ".0" so whole counts read cleanly. */
export function formatParameters(value: number | null | undefined): string {
  if (value === null || value === undefined || !Number.isFinite(value) || value <= 0) {
    return MISSING_VALUE;
  }
  const fmt = (n: number, suffix: string) => `${n.toFixed(1).replace(/\.0$/, '')}${suffix}`;
  if (value >= 1e12) return fmt(value / 1e12, 'T');
  if (value >= 1e9) return fmt(value / 1e9, 'B');
  if (value >= 1e6) return fmt(value / 1e6, 'M');
  if (value >= 1e3) return fmt(value / 1e3, 'K');
  return String(value);
}
